import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { 
  ArrowLeft, 
  CheckCircle, 
  FileText, 
  User, 
  Calendar,
  Building,
  Stamp,
  X,
  AlertCircle,
  Shield,
  Mail,
  Clock,
  QrCode,
  Download,
  Award,
  Stethoscope,
  Eye
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';
import api from '@/services/api';
import { useApp } from '@/context/AppContext';
import DocumentQRCode from '@/components/DocumentQRCode';
import DocumentUpload from '@/components/DocumentUpload';

const StatusBadge = ({ statut }) => {
  const config = {
    'Délivrée': { className: 'bg-green-100 text-green-800', icon: CheckCircle },
    'En signature': { className: 'bg-blue-100 text-blue-800', icon: Clock },
    'À signer': { className: 'bg-blue-100 text-blue-800', icon: Clock },
    'À valider HSE': { className: 'bg-orange-100 text-orange-800', icon: AlertCircle },
    'Brouillon': { className: 'bg-gray-100 text-gray-800', icon: FileText },
    'Révoquée': { className: 'bg-red-100 text-red-800', icon: X },
    'Rejetée': { className: 'bg-red-100 text-red-800', icon: X },
    'Clôturée': { className: 'bg-gray-100 text-gray-800', icon: FileText },
    'Échue': { className: 'bg-red-100 text-red-800', icon: X }
  };
  const { className, icon: Icon } = config[statut] || { className: 'bg-gray-100 text-gray-800' };
  return (
    <Badge className={className}>
      {Icon && <Icon className="h-3 w-3 mr-1" />}
      {statut}
    </Badge>
  );
};

export default function DemandeDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useApp();
  
  const [demande, setDemande] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authenticiteVerifiee, setAuthenticiteVerifiee] = useState(false);
  const [motifRefus, setMotifRefus] = useState('');
  const [dateFinAutorisation, setDateFinAutorisation] = useState('');
  const [cacesDetails, setCacesDetails] = useState([]);
  const [visiteDetails, setVisiteDetails] = useState(null);
  const [cacesDocsDialogOpen, setCacesDocsDialogOpen] = useState(false);
  const [visiteDocsDialogOpen, setVisiteDocsDialogOpen] = useState(false);
  const [selectedCacesId, setSelectedCacesId] = useState(null);
  
  // OTP dialog
  const [otpDialogOpen, setOtpDialogOpen] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [displayedOtpCode, setDisplayedOtpCode] = useState(''); // Code to display when email fails
  const [signatureData, setSignatureData] = useState('');
  const [pendingAction, setPendingAction] = useState(null);

  useEffect(() => {
    loadDemande();
  }, [id]);

  const loadDemande = async () => {
    try {
      setLoading(true);
      const data = await api.getAutorisation(id);
      
      // Load collaborateur details
      if (data.collaborateur_id) {
        try {
          const collab = await api.getCollaborateur(data.collaborateur_id);
          data.collaborateur = collab;
        } catch (e) {
          console.error('Error loading collaborateur details:', e);
        }
      }
      
      setDemande(data);
      if (data.date_fin) {
        setDateFinAutorisation(data.date_fin);
      }
      
      // Load CACES details
      if (data.caces_ids && data.caces_ids.length > 0) {
        try {
          const allCaces = await api.getAllCACES();
          const relatedCaces = allCaces.filter(c => data.caces_ids.includes(c.id));
          setCacesDetails(relatedCaces);
        } catch (e) {
          console.error('Error loading CACES details:', e);
        }
      }
      
      // Load visite médicale details
      if (data.visite_medicale_id && data.collaborateur_id) {
        try {
          const collab = data.collaborateur || await api.getCollaborateur(data.collaborateur_id);
          if (collab.visites_medicales) {
            const visite = collab.visites_medicales.find(v => v.id === data.visite_medicale_id);
            if (visite) setVisiteDetails(visite);
          }
        } catch (e) {
          console.error('Error loading visite details:', e);
        }
      }
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleValidateHSE = async () => {
    if (!authenticiteVerifiee) {
      toast.error('Veuillez vérifier l\'authenticité du CACES');
      return;
    }
    try {
      await api.validateAutorisationHSE(id, {
        user_id: currentUser.id,
        user_nom: `${currentUser.prenom} ${currentUser.nom}`,
        user_role: currentUser.role
      });
      toast.success('Demande validée par HSE et soumise au Directeur');
      loadDemande();
    } catch (error) {
      toast.error(error.message || 'Erreur lors de la validation');
    }
  };

  const handleRequestOTP = async () => {
    try {
      const result = await api.generateOTP(currentUser.id, 'signature');
      setOtpSent(true);
      
      // Check if email was sent successfully
      if (result.email_sent) {
        toast.success(`Code OTP envoyé à ${currentUser.email}`);
        setDisplayedOtpCode('');
      } else if (result.otp_code) {
        // Email failed, show the code directly (dev/test mode)
        setDisplayedOtpCode(result.otp_code);
        toast.warning('Email non disponible - Le code est affiché ci-dessous', { duration: 10000 });
      }
    } catch (error) {
      toast.error('Erreur lors de l\'envoi du code OTP');
    }
  };

  const handleVerifyOTPAndSign = async () => {
    if (!otpCode || otpCode.length !== 6) {
      toast.error('Veuillez entrer un code à 6 chiffres');
      return;
    }

    try {
      // Verify OTP
      await api.verifyOTP(currentUser.id, otpCode, 'signature');
      
      // Sign authorization
      await api.signAutorisation(id, {
        user_id: currentUser.id,
        user_nom: `${currentUser.prenom} ${currentUser.nom}`,
        user_role: currentUser.role,
        signature_data: signatureData || 'signature_electronique',
        otp_verifie: true
      });
      
      toast.success('Autorisation signée et délivrée avec succès!');
      setOtpDialogOpen(false);
      setOtpCode('');
      setOtpSent(false);
      loadDemande();
    } catch (error) {
      toast.error(error.message || 'Code OTP invalide');
    }
  };

  const handleSubmitToHSE = async () => {
    if (!dateFinAutorisation) {
      toast.error('Veuillez sélectionner une date de fin d\'autorisation');
      return;
    }
    try {
      // First update the date, then submit
      await api.updateAutorisation(id, {
        date_fin: dateFinAutorisation
      });
      // Then submit to HSE
      await api.submitAutorisation(id);
      toast.success('Demande soumise à HSE pour validation');
      loadDemande();
    } catch (error) {
      toast.error(error.message || 'Erreur lors de la soumission');
    }
  };

  const handleReject = async () => {
    if (!motifRefus) {
      toast.error('Veuillez indiquer un motif de rejet');
      return;
    }
    try {
      await api.rejectAutorisation(id, { 
        motif: motifRefus,
        user_id: currentUser?.id,
        user_nom: `${currentUser?.prenom || ''} ${currentUser?.nom || ''}`.trim(),
        user_role: currentUser?.role
      });
      toast.success('Demande rejetée');
      setMotifRefus('');
      loadDemande();
    } catch (error) {
      toast.error(error.message || 'Erreur lors du rejet');
    }
  };

  const handleClose = async () => {
    try {
      await api.closeAutorisation(id, currentUser?.id, `${currentUser?.prenom || ''} ${currentUser?.nom || ''}`.trim());
      toast.success('Demande clôturée');
      loadDemande();
    } catch (error) {
      toast.error(error.message || 'Erreur lors de la clôture');
    }
  };

  const handleRevoke = async () => {
    if (!motifRefus) {
      toast.error('Veuillez indiquer un motif');
      return;
    }
    try {
      await api.revokeAutorisation(id, { motif: motifRefus });
      toast.success('Autorisation révoquée');
      loadDemande();
    } catch (error) {
      toast.error('Erreur lors de la révocation');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!demande) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <FileText className="w-16 h-16 mx-auto mb-4 text-gray-400" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Demande non trouvée</h2>
          <Link to="/demandes">
            <Button>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retour aux demandes
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const collaborateur = demande.collaborateur || {};

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Demande {demande.numero}
            </h1>
            <p className="text-gray-500">
              {collaborateur.prenom} {collaborateur.nom}
            </p>
          </div>
        </div>
        <StatusBadge statut={demande.statut} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Collaborateur Info */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5" />
                Collaborateur
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Nom complet</p>
                  <p className="font-medium">{collaborateur.prenom} {collaborateur.nom}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Matricule</p>
                  <p className="font-medium">{collaborateur.matricule}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Secteur</p>
                  <p className="font-medium">{collaborateur.secteur}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Poste</p>
                  <p className="font-medium">{collaborateur.poste}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Catégories demandées */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5" />
                Catégories demandées
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {demande.categories?.map((cat, i) => (
                  <Badge key={i} variant="outline" className="text-base py-1 px-3">
                    {cat}
                  </Badge>
                ))}
                {(!demande.categories || demande.categories.length === 0) && demande.categories_caces_codes?.map((cat, i) => (
                  <Badge key={i} variant="outline" className="text-base py-1 px-3">
                    {cat}
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Détails des CACES */}
          {cacesDetails.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Award className="h-5 w-5 text-blue-600" />
                  Certificats CACES associés
                </CardTitle>
                <CardDescription>Vérification des certificats pour cette autorisation</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Catégorie</TableHead>
                      <TableHead>Organisme</TableHead>
                      <TableHead>Validité</TableHead>
                      <TableHead>Documents</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {cacesDetails.map((caces) => (
                      <TableRow key={caces.id}>
                        <TableCell>
                          <Badge className="bg-blue-100 text-blue-800">{caces.categorie_code}</Badge>
                        </TableCell>
                        <TableCell>{caces.organisme || '-'}</TableCell>
                        <TableCell>
                          <div className="text-sm">
                            <p>Du {new Date(caces.date_obtention).toLocaleDateString('fr-FR')}</p>
                            <p>Au {new Date(caces.date_fin).toLocaleDateString('fr-FR')}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => {
                              setSelectedCacesId(caces.id);
                              setCacesDocsDialogOpen(true);
                            }}
                          >
                            <Eye className="h-4 w-4 mr-1" />
                            Voir
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}

          {/* Détails de la visite médicale */}
          {visiteDetails && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Stethoscope className="h-5 w-5 text-green-600" />
                  Visite médicale associée
                </CardTitle>
                <CardDescription>Vérification de l'aptitude médicale</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-500">Type de visite</p>
                      <p className="font-medium">{visiteDetails.type_visite_libelle || '-'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Médecin</p>
                      <p className="font-medium">{visiteDetails.medecin || '-'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Date de visite</p>
                      <p className="font-medium">
                        {visiteDetails.date_visite ? new Date(visiteDetails.date_visite).toLocaleDateString('fr-FR') : '-'}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Validité jusqu'au</p>
                      <p className="font-medium">
                        {visiteDetails.date_fin ? new Date(visiteDetails.date_fin).toLocaleDateString('fr-FR') : '-'}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Aptitude</p>
                      <Badge className={visiteDetails.apte ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                        {visiteDetails.apte ? '✓ Apte' : '✗ Inapte'}
                      </Badge>
                    </div>
                  </div>
                  <Separator />
                  <Button 
                    variant="outline" 
                    className="w-full"
                    onClick={() => setVisiteDocsDialogOpen(true)}
                  >
                    <Eye className="h-4 w-4 mr-2" />
                    Voir les documents de la visite
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Workflow Timeline */}
          <Card>
            <CardHeader>
              <CardTitle>Historique du workflow</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <div className={`p-2 rounded-full ${demande.date_creation ? 'bg-green-100' : 'bg-gray-100'}`}>
                    <FileText className={`h-4 w-4 ${demande.date_creation ? 'text-green-600' : 'text-gray-400'}`} />
                  </div>
                  <div>
                    <p className="font-medium">Création</p>
                    <p className="text-sm text-gray-500">
                      {demande.date_creation ? new Date(demande.date_creation).toLocaleString('fr-FR') : '-'}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center gap-4">
                  <div className={`p-2 rounded-full ${demande.valide_hse_date ? 'bg-green-100' : 'bg-gray-100'}`}>
                    <CheckCircle className={`h-4 w-4 ${demande.valide_hse_date ? 'text-green-600' : 'text-gray-400'}`} />
                  </div>
                  <div>
                    <p className="font-medium">Validation HSE</p>
                    <p className="text-sm text-gray-500">
                      {demande.valide_hse_date ? (
                        <>{demande.valide_hse_par_nom} - {new Date(demande.valide_hse_date).toLocaleString('fr-FR')}</>
                      ) : 'En attente'}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center gap-4">
                  <div className={`p-2 rounded-full ${demande.statut === 'Délivrée' ? 'bg-green-100' : 'bg-gray-100'}`}>
                    <Stamp className={`h-4 w-4 ${demande.statut === 'Délivrée' ? 'text-green-600' : 'text-gray-400'}`} />
                  </div>
                  <div>
                    <p className="font-medium">Signature Directeur</p>
                    <p className="text-sm text-gray-500">
                      {demande.statut === 'Délivrée' ? (
                        <>{demande.signe_par_nom || 'Directeur'} - {demande.signe_date ? new Date(demande.signe_date).toLocaleString('fr-FR') : ''}</>
                      ) : 'En attente'}
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar - Actions */}
        <div className="space-y-6">
          {/* Dates */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="h-5 w-5" />
                Dates
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <p className="text-sm text-gray-500">Date de création</p>
                <p className="font-medium">
                  {demande.date_creation ? new Date(demande.date_creation).toLocaleDateString('fr-FR') : '-'}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Date de délivrance</p>
                <p className="font-medium">
                  {demande.date_delivrance ? new Date(demande.date_delivrance).toLocaleDateString('fr-FR') : '-'}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Date d'expiration</p>
                <p className="font-medium">
                  {demande.date_fin ? new Date(demande.date_fin).toLocaleDateString('fr-FR') : '-'}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Actions based on status */}
          {demande.statut === 'Brouillon' && (
            <Card>
              <CardHeader>
                <CardTitle>Soumettre la demande</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Date de fin d'autorisation</Label>
                  <Input
                    type="date"
                    value={dateFinAutorisation}
                    onChange={(e) => setDateFinAutorisation(e.target.value)}
                  />
                </div>
                <Button onClick={handleSubmitToHSE} className="w-full">
                  Soumettre à HSE
                </Button>
              </CardContent>
            </Card>
          )}

          {demande.statut === 'À valider HSE' && (currentUser?.role === 'HSE' || currentUser?.role === 'Admin') && (
            <Card>
              <CardHeader>
                <CardTitle>Validation HSE</CardTitle>
                <CardDescription>Vérifiez les documents et validez</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="authenticite"
                    checked={authenticiteVerifiee}
                    onCheckedChange={setAuthenticiteVerifiee}
                  />
                  <Label htmlFor="authenticite" className="text-sm">
                    J'ai vérifié l'authenticité du CACES et la validité de la visite médicale
                  </Label>
                </div>
                <Separator />
                <Button onClick={handleValidateHSE} className="w-full" disabled={!authenticiteVerifiee}>
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Valider et envoyer au Directeur
                </Button>
              </CardContent>
            </Card>
          )}

          {(demande.statut === 'En signature' || demande.statut === 'À signer') && (currentUser?.role === 'Directeur' || currentUser?.role === 'Admin') && (
            <Card>
              <CardHeader>
                <CardTitle>Signature Directeur</CardTitle>
                <CardDescription>Double authentification requise</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <Alert>
                  <Mail className="h-4 w-4" />
                  <AlertDescription>
                    Un code OTP sera envoyé à votre adresse email pour confirmer la signature.
                  </AlertDescription>
                </Alert>
                <Button onClick={() => setOtpDialogOpen(true)} className="w-full">
                  <Stamp className="h-4 w-4 mr-2" />
                  Signer l'autorisation
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Message si l'utilisateur n'a pas le droit de valider/signer */}
          {demande.statut === 'À valider HSE' && currentUser?.role !== 'HSE' && currentUser?.role !== 'Admin' && (
            <Card className="border-orange-200 bg-orange-50">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 text-orange-800">
                  <AlertCircle className="h-5 w-5" />
                  <span>En attente de validation par le responsable HSE</span>
                </div>
              </CardContent>
            </Card>
          )}

          {(demande.statut === 'En signature' || demande.statut === 'À signer') && currentUser?.role !== 'Directeur' && currentUser?.role !== 'Admin' && (
            <Card className="border-blue-200 bg-blue-50">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 text-blue-800">
                  <AlertCircle className="h-5 w-5" />
                  <span>En attente de signature par le Directeur</span>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Reject Section - For HSE/Directeur/Admin when status is À valider HSE or À signer */}
          {(demande.statut === 'À valider HSE' || demande.statut === 'À signer') && 
           (currentUser?.role === 'HSE' || currentUser?.role === 'Directeur' || currentUser?.role === 'Admin') && (
            <Card className="border-red-200">
              <CardHeader>
                <CardTitle className="text-red-800">Rejeter la demande</CardTitle>
                <CardDescription>Refuser cette demande d'autorisation</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Motif de rejet</Label>
                  <Textarea
                    value={motifRefus}
                    onChange={(e) => setMotifRefus(e.target.value)}
                    placeholder="Indiquez le motif du rejet..."
                  />
                </div>
                <Button variant="destructive" onClick={handleReject} className="w-full">
                  <X className="h-4 w-4 mr-2" />
                  Rejeter la demande
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Close Section - For Admin, HSE, or Manager when status is Brouillon, Rejetée or Révoquée */}
          {(demande.statut === 'Brouillon' || demande.statut === 'Rejetée' || demande.statut === 'Révoquée') && 
           (currentUser?.role === 'Admin' || currentUser?.role === 'HSE' || currentUser?.role === 'Manager') && (
            <Card>
              <CardHeader>
                <CardTitle>Annuler / Clôturer le ticket</CardTitle>
                <CardDescription>Annuler cette demande pour en créer une nouvelle</CardDescription>
              </CardHeader>
              <CardContent>
                <Button variant="outline" onClick={handleClose} className="w-full">
                  <FileText className="h-4 w-4 mr-2" />
                  Annuler et archiver
                </Button>
              </CardContent>
            </Card>
          )}

          {demande.statut === 'Délivrée' && (
            <Card className="border-green-200 bg-green-50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-green-800">
                  <QrCode className="h-5 w-5" />
                  QR Code de vérification
                </CardTitle>
                <CardDescription>
                  Générez un QR code pour vérifier l'authenticité de cette autorisation
                </CardDescription>
              </CardHeader>
              <CardContent>
                <DocumentQRCode 
                  docType="autorisation" 
                  docId={demande.id}
                  documentTitle={`Autorisation ${demande.numero}`}
                />
              </CardContent>
            </Card>
          )}

          {demande.statut === 'Délivrée' && (
            <Card>
              <CardHeader>
                <CardTitle>Révoquer</CardTitle>
                <CardDescription>Révoquer cette autorisation</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Motif de révocation</Label>
                  <Textarea
                    value={motifRefus}
                    onChange={(e) => setMotifRefus(e.target.value)}
                    placeholder="Indiquez le motif..."
                  />
                </div>
                <Button variant="destructive" onClick={handleRevoke} className="w-full">
                  <X className="h-4 w-4 mr-2" />
                  Révoquer
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Documents joints */}
          <DocumentUpload 
            entityType="demande_hse" 
            entityId={demande.id}
            title="Documents justificatifs"
          />
        </div>
      </div>

      {/* OTP Dialog */}
      <Dialog open={otpDialogOpen} onOpenChange={setOtpDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Signature électronique - Double authentification</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {!otpSent ? (
              <>
                <p className="text-sm text-gray-500">
                  Un code de vérification à 6 chiffres sera envoyé à votre adresse email 
                  ({currentUser?.email}) pour confirmer votre signature.
                </p>
                <Button onClick={handleRequestOTP} className="w-full">
                  <Mail className="h-4 w-4 mr-2" />
                  Envoyer le code OTP
                </Button>
              </>
            ) : (
              <>
                <p className="text-sm text-gray-500">
                  Entrez le code à 6 chiffres envoyé à {currentUser?.email}
                </p>
                
                {/* Display OTP code when email fails (dev/test mode) */}
                {displayedOtpCode && (
                  <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                    <p className="text-sm text-amber-800 font-medium mb-2">
                      ⚠️ Email non disponible - Votre code OTP :
                    </p>
                    <p className="text-3xl font-mono font-bold text-amber-900 tracking-[0.5em] text-center">
                      {displayedOtpCode}
                    </p>
                    <p className="text-xs text-amber-600 mt-2">
                      Pour activer l'envoi email, configurez une clé Resend avec domaine vérifié
                    </p>
                  </div>
                )}
                
                <div>
                  <Label>Code OTP</Label>
                  <Input
                    value={otpCode}
                    onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="000000"
                    className="text-center text-2xl tracking-widest font-mono"
                    maxLength={6}
                  />
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={handleRequestOTP} className="flex-1">
                    Renvoyer
                  </Button>
                  <Button onClick={handleVerifyOTPAndSign} className="flex-1" disabled={otpCode.length !== 6}>
                    <Stamp className="h-4 w-4 mr-2" />
                    Signer
                  </Button>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* CACES Documents Dialog */}
      <Dialog open={cacesDocsDialogOpen} onOpenChange={setCacesDocsDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Award className="h-5 w-5 text-blue-600" />
              Documents CACES
            </DialogTitle>
          </DialogHeader>
          {selectedCacesId && (
            <DocumentUpload 
              entityType="caces" 
              entityId={selectedCacesId}
              title="Justificatifs du certificat CACES"
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Visite Médicale Documents Dialog */}
      <Dialog open={visiteDocsDialogOpen} onOpenChange={setVisiteDocsDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Stethoscope className="h-5 w-5 text-green-600" />
              Documents Visite Médicale
            </DialogTitle>
          </DialogHeader>
          {visiteDetails && (
            <DocumentUpload 
              entityType="visite" 
              entityId={visiteDetails.id}
              title="Justificatifs de la visite médicale"
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
